
import React, { useState, useEffect, useMemo } from 'react';
import util from '../../utils/util';
import { getApiBase } from '../../utils/axios';

// ─── Event type display config ────────────────────────────────
const EVENT_META = {
    page_view:              { label: 'Page View',          icon: 'fa-eye',               color: '#8c8c8c', bg: '#f5f5f5' },
    tab_change:             { label: 'Tab Change',          icon: 'fa-exchange',          color: '#595959', bg: '#f5f5f5' },
    link_click:             { label: 'Link Click',          icon: 'fa-mouse-pointer',     color: '#595959', bg: '#f5f5f5' },
    login:                  { label: 'Login',               icon: 'fa-sign-in',           color: '#52c41a', bg: '#f6ffed' },
    logout:                 { label: 'Logout',              icon: 'fa-sign-out',          color: '#fa8c16', bg: '#fff7e6' },
    record_create:          { label: 'Created',             icon: 'fa-plus-circle',       color: '#52c41a', bg: '#f6ffed' },
    record_update:          { label: 'Updated',             icon: 'fa-pencil',            color: '#1890ff', bg: '#e6f7ff' },
    record_delete:          { label: 'Deleted',             icon: 'fa-trash',             color: '#ff4d4f', bg: '#fff2f0' },
    record_view:            { label: 'Viewed',              icon: 'fa-eye',               color: '#8c8c8c', bg: '#f5f5f5' },
    form_submit:            { label: 'Form Submit',         icon: 'fa-check-square-o',    color: '#1890ff', bg: '#e6f7ff' },
    form_field_change:      { label: 'Field Changed',       icon: 'fa-keyboard-o',        color: '#8c8c8c', bg: '#f5f5f5' },
    form_validation_fail:   { label: 'Validation Failed',   icon: 'fa-warning',           color: '#fa8c16', bg: '#fff7e6' },
    file_upload:            { label: 'File Uploaded',       icon: 'fa-upload',            color: '#722ed1', bg: '#f9f0ff' },
    file_download:          { label: 'File Downloaded',     icon: 'fa-download',          color: '#13c2c2', bg: '#e6fffb' },
    file_delete:            { label: 'File Deleted',        icon: 'fa-times-circle',      color: '#ff4d4f', bg: '#fff2f0' },
    document_view:          { label: 'Document Viewed',     icon: 'fa-file-text-o',       color: '#8c8c8c', bg: '#f5f5f5' },
    report_generate:        { label: 'Report Generated',    icon: 'fa-bar-chart',         color: '#13c2c2', bg: '#e6fffb' },
    report_export:          { label: 'Report Exported',     icon: 'fa-file-excel-o',      color: '#52c41a', bg: '#f6ffed' },
    report_pdf:             { label: 'PDF Generated',       icon: 'fa-file-pdf-o',        color: '#ff4d4f', bg: '#fff2f0' },
    search:                 { label: 'Searched',            icon: 'fa-search',            color: '#8c8c8c', bg: '#f5f5f5' },
    filter_apply:           { label: 'Filter Applied',      icon: 'fa-filter',            color: '#8c8c8c', bg: '#f5f5f5' },
    error:                  { label: 'Error',               icon: 'fa-exclamation-circle',color: '#ff4d4f', bg: '#fff2f0' },
    api_fail:               { label: 'API Failure',         icon: 'fa-wifi',              color: '#ff4d4f', bg: '#fff2f0' },
    // QoC-specific
    assessment_submit:      { label: 'Assessment Submitted',icon: 'fa-send',              color: '#1890ff', bg: '#e6f7ff' },
    assessment_approve:     { label: 'Assessment Approved', icon: 'fa-check-circle',      color: '#52c41a', bg: '#f6ffed' },
    assessment_reject:      { label: 'Assessment Rejected', icon: 'fa-times-circle',      color: '#ff4d4f', bg: '#fff2f0' },
    assessment_revision:    { label: 'Revision Requested',  icon: 'fa-undo',              color: '#fa8c16', bg: '#fff7e6' },
    cap_create:             { label: 'CAP Created',         icon: 'fa-plus',              color: '#722ed1', bg: '#f9f0ff' },
    cap_update:             { label: 'CAP Updated',         icon: 'fa-pencil',            color: '#1890ff', bg: '#e6f7ff' },
    standard_setup:         { label: 'Standards Changed',   icon: 'fa-cog',               color: '#fa8c16', bg: '#fff7e6' },
    hie_sync:               { label: 'HIE Sync',            icon: 'fa-refresh',           color: '#13c2c2', bg: '#e6fffb' },
};

const CRITICAL_EVENTS = new Set([
    'record_delete', 'file_delete', 'error', 'api_fail',
    'assessment_reject', 'assessment_revision',
]);

const HIGH_PRIORITY_EVENTS = new Set([
    'assessment_submit', 'assessment_approve', 'assessment_reject',
    'assessment_revision', 'cap_create', 'standard_setup', 'hie_sync', 'login', 'logout',
]);

// ─── Helpers ──────────────────────────────────────────────────
const formatDT = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString('en-SZ', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
};

const parseDetails = (details) => {
    if (!details) return null;
    if (typeof details === 'object') return details;
    try { return JSON.parse(details); } catch { return { raw: details }; }
};

// ─── Sub-components ───────────────────────────────────────────
const EventBadge = ({ eventType }) => {
    const m = EVENT_META[eventType] || { label: eventType, icon: 'fa-circle', color: '#8c8c8c', bg: '#f5f5f5' };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '2px 9px', borderRadius: '12px', fontSize: '11px',
            fontWeight: '600', color: m.color, background: m.bg,
            border: `1px solid ${m.color}33`, whiteSpace: 'nowrap',
        }}>
            <i className={`fa ${m.icon}`}></i> {m.label}
        </span>
    );
};

const StatCard = ({ icon, label, value, color }) => (
    <div style={{
        background: '#fff', borderRadius: '8px', border: '1px solid #e8e8e8',
        padding: '16px', display: 'flex', alignItems: 'center', gap: '14px',
    }}>
        <div style={{
            width: '44px', height: '44px', borderRadius: '10px',
            background: `${color}18`, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0,
        }}>
            <i className={`fa ${icon}`} style={{ fontSize: '20px', color }}></i>
        </div>
        <div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: '#262626', lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '3px' }}>{label}</div>
        </div>
    </div>
);

// ─── Main component ───────────────────────────────────────────
const UserActivityReport = () => {
    const isAdmin = util.isAdmin() || util.isNational();

    const [logs, setLogs]             = useState([]);
    const [loading, setLoading]       = useState(false);
    const [users, setUsers]           = useState([]);
    const [modules, setModules]       = useState([]);
    const [expandedLog, setExpandedLog] = useState(null);
    const [filters, setFilters]       = useState({
        user: '', eventType: '', module: '',
        dateFrom: '', dateTo: '', k: '', p: 1, ps: 50,
    });
    const [pagination, setPagination] = useState({ total: 0, current_page: 1, per_page: 50 });

    useEffect(() => { loadLogs(); }, [filters]);

    const loadLogs = async () => {
        try {
            setLoading(true);
            const apiBase = getApiBase();
            const params = new URLSearchParams();
            if (filters.user)      params.set('user',      filters.user);
            if (filters.eventType) params.set('eventType', filters.eventType);
            if (filters.module)    params.set('module',    filters.module);
            if (filters.dateFrom)  params.set('dateFrom',  filters.dateFrom);
            if (filters.dateTo)    params.set('dateTo',    filters.dateTo);
            if (filters.k)         params.set('k',         filters.k);
            params.set('p',  filters.p);
            params.set('ps', filters.ps);

            const res = await fetch(apiBase + 'qoc/activity-trail?' + params.toString(), {
                headers: { authorization: window.localStorage['token'] || '' },
            }).then(r => r.json());

            if (res.code === 200) {
                const data = res.result?.data || [];
                setLogs(data);
                setPagination({
                    total:        parseInt(res.result?.total        || 0),
                    current_page: parseInt(res.result?.current_page || 1),
                    per_page:     parseInt(res.result?.per_page     || 50),
                });
                // Derive unique user + module lists for filter dropdowns
                setUsers(prev => {
                    const all = new Set([...prev, ...data.map(l => l.user).filter(Boolean)]);
                    return [...all].sort();
                });
                setModules(prev => {
                    const all = new Set([...prev, ...data.map(l => l.module).filter(Boolean)]);
                    return [...all].sort();
                });
            }
        } catch (e) {
            console.error('Error loading activity trail:', e);
        } finally {
            setLoading(false);
        }
    };

    // ─── Derived analytics ─────────────────────────────────────
    const analytics = useMemo(() => {
        const byEvent  = {};
        const byModule = {};
        const byUser   = {};
        let criticalCount = 0;
        let highPriorityCount = 0;

        logs.forEach(log => {
            byEvent[log.eventType]  = (byEvent[log.eventType]  || 0) + 1;
            if (log.module) byModule[log.module] = (byModule[log.module] || 0) + 1;
            if (log.user)   byUser[log.user]     = (byUser[log.user]     || 0) + 1;
            if (CRITICAL_EVENTS.has(log.eventType))     criticalCount++;
            if (HIGH_PRIORITY_EVENTS.has(log.eventType)) highPriorityCount++;
        });

        return {
            topModules:      Object.entries(byModule).sort((a, b) => b[1] - a[1]).slice(0, 6),
            topUsers:        Object.entries(byUser).sort((a, b)   => b[1] - a[1]).slice(0, 5),
            topEvents:       Object.entries(byEvent).sort((a, b)  => b[1] - a[1]).slice(0, 6),
            uniqueUserCount: Object.keys(byUser).length,
            moduleCount:     Object.keys(byModule).length,
            criticalCount,
            highPriorityCount,
        };
    }, [logs]);

    // ─── CSV export ────────────────────────────────────────────
    const exportCSV = () => {
        const headers = ['Timestamp', 'User', 'Event', 'Module', 'Action', 'Device', 'IP Address', 'URL', 'Details'];
        const rows = logs.map(l => [
            l.timestamp || '',
            l.user      || '',
            l.eventType || '',
            l.module    || '',
            l.action    || '',
            l.deviceType || '',
            l.ipAddress  || '',
            l.url        || '',
            l.details ? (typeof l.details === 'string' ? l.details : JSON.stringify(l.details)) : '',
        ]);
        const csv = [headers, ...rows]
            .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `qoc_activity_trail_${filters.user || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const totalPages = Math.ceil(pagination.total / pagination.per_page);

    // ─── Render ────────────────────────────────────────────────
    return (
        <div className="page-content">

            {/* Header */}
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h1>Activity Trail</h1>
                    <p style={{ margin: 0, color: '#8c8c8c', fontSize: '14px' }}>
                        Complete audit trail of all user actions across the QoC platform
                    </p>
                </div>
                <div className="ml-auto my-auto d-flex" style={{ gap: '8px' }}>
                    <button className="btn btn-default" onClick={exportCSV} disabled={logs.length === 0}>
                        <i className="fa fa-file-excel-o"></i> Export CSV
                    </button>
                    <button className="btn btn-primary" onClick={loadLogs}>
                        <i className="fa fa-refresh"></i> Refresh
                    </button>
                </div>
            </div>

            <div className="page-pad">

                {/* Filters */}
                <div className="card" style={{ marginBottom: '16px' }}>
                    <div className="card-header" style={{ padding: '12px 16px' }}>
                        <strong><i className="fa fa-filter" style={{ marginRight: '6px' }}></i> Filters</strong>
                    </div>
                    <div className="card-body" style={{ padding: '14px 16px' }}>
                        <div className="row">

                            {/* User filter — admin only */}
                            {isAdmin && (
                                <div className="col-md-2">
                                    <label className="control-label" style={{ fontSize: '12px', color: '#8c8c8c' }}>User</label>
                                    <select className="form-control input-sm"
                                        value={filters.user}
                                        onChange={e => setFilters(p => ({ ...p, user: e.target.value, p: 1 }))}>
                                        <option value="">All Users</option>
                                        {users.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                </div>
                            )}

                            {/* Event type filter */}
                            <div className="col-md-2">
                                <label className="control-label" style={{ fontSize: '12px', color: '#8c8c8c' }}>Event Type</label>
                                <select className="form-control input-sm"
                                    value={filters.eventType}
                                    onChange={e => setFilters(p => ({ ...p, eventType: e.target.value, p: 1 }))}>
                                    <option value="">All Events</option>
                                    {Object.entries(EVENT_META).map(([k, v]) => (
                                        <option key={k} value={k}>{v.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Module filter */}
                            <div className="col-md-2">
                                <label className="control-label" style={{ fontSize: '12px', color: '#8c8c8c' }}>Module</label>
                                <select className="form-control input-sm"
                                    value={filters.module}
                                    onChange={e => setFilters(p => ({ ...p, module: e.target.value, p: 1 }))}>
                                    <option value="">All Modules</option>
                                    {modules.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </div>

                            {/* Date from */}
                            <div className="col-md-2">
                                <label className="control-label" style={{ fontSize: '12px', color: '#8c8c8c' }}>From Date</label>
                                <input type="date" className="form-control input-sm"
                                    value={filters.dateFrom}
                                    onChange={e => setFilters(p => ({ ...p, dateFrom: e.target.value, p: 1 }))} />
                            </div>

                            {/* Date to */}
                            <div className="col-md-2">
                                <label className="control-label" style={{ fontSize: '12px', color: '#8c8c8c' }}>To Date</label>
                                <input type="date" className="form-control input-sm"
                                    value={filters.dateTo}
                                    onChange={e => setFilters(p => ({ ...p, dateTo: e.target.value, p: 1 }))} />
                            </div>

                            {/* Keyword search */}
                            <div className="col-md-2">
                                <label className="control-label" style={{ fontSize: '12px', color: '#8c8c8c' }}>Keyword</label>
                                <div className="input-group input-group-sm">
                                    <input type="text" className="form-control"
                                        placeholder="Search actions..."
                                        value={filters.k}
                                        onChange={e => setFilters(p => ({ ...p, k: e.target.value, p: 1 }))} />
                                    {filters.k && (
                                        <span className="input-group-btn">
                                            <button className="btn btn-default" onClick={() => setFilters(p => ({ ...p, k: '', p: 1 }))}>
                                                <i className="fa fa-times"></i>
                                            </button>
                                        </span>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                {/* Stat cards */}
                {logs.length > 0 && (
                    <div className="row" style={{ marginBottom: '16px' }}>
                        <div className="col-md-3">
                            <StatCard icon="fa-list"                 label="Total Events"       value={pagination.total.toLocaleString()} color="#1890ff" />
                        </div>
                        <div className="col-md-3">
                            <StatCard icon="fa-users"                label="Unique Users"        value={analytics.uniqueUserCount}        color="#52c41a" />
                        </div>
                        <div className="col-md-3">
                            <StatCard icon="fa-sitemap"              label="Modules Touched"     value={analytics.moduleCount}            color="#722ed1" />
                        </div>
                        <div className="col-md-3">
                            <StatCard icon="fa-exclamation-triangle" label="Critical Actions"    value={analytics.criticalCount}          color="#ff4d4f" />
                        </div>
                    </div>
                )}

                {/* Breakdown cards */}
                {logs.length > 0 && (
                    <div className="row" style={{ marginBottom: '16px' }}>

                        {/* Top modules */}
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-header" style={{ padding: '10px 14px' }}>
                                    <strong style={{ fontSize: '13px' }}>
                                        <i className="fa fa-sitemap" style={{ marginRight: '6px', color: '#722ed1' }}></i> Top Modules
                                    </strong>
                                </div>
                                <div className="card-body" style={{ padding: '10px 14px' }}>
                                    {analytics.topModules.map(([mod, cnt]) => (
                                        <div key={mod} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                            <span style={{ fontSize: '13px', color: '#262626' }}>{mod}</span>
                                            <span style={{ fontWeight: '700', fontSize: '13px', color: '#722ed1', background: '#f9f0ff', padding: '1px 8px', borderRadius: '10px' }}>{cnt}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Top event types */}
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-header" style={{ padding: '10px 14px' }}>
                                    <strong style={{ fontSize: '13px' }}>
                                        <i className="fa fa-bolt" style={{ marginRight: '6px', color: '#fa8c16' }}></i> Event Breakdown
                                    </strong>
                                </div>
                                <div className="card-body" style={{ padding: '10px 14px' }}>
                                    {analytics.topEvents.map(([evt, cnt]) => {
                                        const m = EVENT_META[evt] || { label: evt, color: '#8c8c8c', icon: 'fa-circle', bg: '#f5f5f5' };
                                        return (
                                            <div key={evt} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                                                    <i className={`fa ${m.icon}`} style={{ color: m.color, width: '14px' }}></i>
                                                    {m.label}
                                                </span>
                                                <span style={{ fontWeight: '700', fontSize: '13px', color: m.color, background: m.bg, padding: '1px 8px', borderRadius: '10px' }}>{cnt}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Most active users */}
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-header" style={{ padding: '10px 14px' }}>
                                    <strong style={{ fontSize: '13px' }}>
                                        <i className="fa fa-users" style={{ marginRight: '6px', color: '#52c41a' }}></i> Most Active Users
                                    </strong>
                                </div>
                                <div className="card-body" style={{ padding: '10px 14px' }}>
                                    {analytics.topUsers.map(([user, cnt], idx) => (
                                        <div key={user} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <div style={{
                                                    width: '22px', height: '22px', borderRadius: '50%',
                                                    background: ['#1890ff','#52c41a','#fa8c16','#722ed1','#eb2f96'][idx] || '#8c8c8c',
                                                    color: '#fff', display: 'flex', alignItems: 'center',
                                                    justifyContent: 'center', fontSize: '10px', fontWeight: '700',
                                                }}>
                                                    {idx + 1}
                                                </div>
                                                <span style={{ fontSize: '13px', color: '#262626' }}>{user}</span>
                                            </div>
                                            <span style={{ fontWeight: '700', fontSize: '13px', color: '#52c41a', background: '#f6ffed', padding: '1px 8px', borderRadius: '10px' }}>{cnt}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                    </div>
                )}

                {/* Log table */}
                <div className="card">
                    <div className="card-header" style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong><i className="fa fa-history" style={{ marginRight: '6px' }}></i> Activity Log</strong>
                        <span style={{ fontSize: '13px', color: '#8c8c8c' }}>
                            {loading ? 'Loading...' : `${pagination.total.toLocaleString()} records`}
                        </span>
                    </div>

                    <div className="card-body" style={{ padding: 0 }}>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '60px', color: '#8c8c8c' }}>
                                <i className="fa fa-spinner fa-spin fa-2x"></i>
                                <p style={{ marginTop: '10px' }}>Loading activity trail...</p>
                            </div>
                        ) : logs.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '60px' }}>
                                <i className="fa fa-history" style={{ fontSize: '48px', color: '#d9d9d9' }}></i>
                                <p style={{ marginTop: '12px', color: '#8c8c8c' }}>No activity records found for the selected filters</p>
                            </div>
                        ) : (
                            <table className="table" style={{ margin: 0, fontSize: '13px' }}>
                                <thead style={{ background: '#fafafa' }}>
                                    <tr>
                                        <th style={{ padding: '10px 14px', border: 'none', fontWeight: '600', color: '#595959', width: '155px' }}>Timestamp</th>
                                        <th style={{ padding: '10px 8px',  border: 'none', fontWeight: '600', color: '#595959', width: '120px' }}>User</th>
                                        <th style={{ padding: '10px 8px',  border: 'none', fontWeight: '600', color: '#595959', width: '160px' }}>Event</th>
                                        <th style={{ padding: '10px 8px',  border: 'none', fontWeight: '600', color: '#595959', width: '140px' }}>Module</th>
                                        <th style={{ padding: '10px 8px',  border: 'none', fontWeight: '600', color: '#595959' }}>Action</th>
                                        <th style={{ padding: '10px 8px',  border: 'none', fontWeight: '600', color: '#595959', width: '90px'  }}>Device</th>
                                        <th style={{ padding: '10px 8px',  border: 'none', fontWeight: '600', color: '#595959', width: '110px' }}>IP Address</th>
                                        <th style={{ padding: '10px 14px', border: 'none', width: '36px' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log, idx) => {
                                        const isCritical     = CRITICAL_EVENTS.has(log.eventType);
                                        const isHighPriority = HIGH_PRIORITY_EVENTS.has(log.eventType);
                                        const isExpanded     = expandedLog === log.id;
                                        const details        = parseDetails(log.details);
                                        const hasDetails     = details && Object.keys(details).length > 0;

                                        const rowBg = isCritical
                                            ? '#fff8f8'
                                            : isHighPriority
                                                ? '#f0f7ff'
                                                : idx % 2 === 0 ? '#fff' : '#fafafa';

                                        const leftBorder = isCritical
                                            ? '3px solid #ff4d4f'
                                            : isHighPriority
                                                ? '3px solid #1890ff'
                                                : '3px solid transparent';

                                        return (
                                            <React.Fragment key={log.id || idx}>
                                                <tr style={{ background: rowBg, borderBottom: '1px solid #f0f0f0', borderLeft: leftBorder }}>

                                                    <td style={{ padding: '10px 14px', verticalAlign: 'middle', color: '#595959', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                        {formatDT(log.timestamp)}
                                                    </td>

                                                    <td style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <div style={{
                                                                width: '26px', height: '26px', borderRadius: '50%',
                                                                background: '#1890ff', color: '#fff', display: 'flex',
                                                                alignItems: 'center', justifyContent: 'center',
                                                                fontSize: '11px', fontWeight: '700', flexShrink: 0,
                                                            }}>
                                                                {(log.user || '?')[0].toUpperCase()}
                                                            </div>
                                                            <span style={{ fontWeight: '500', color: '#262626' }}>{log.user || '—'}</span>
                                                        </div>
                                                    </td>

                                                    <td style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                                                        <EventBadge eventType={log.eventType} />
                                                    </td>

                                                    <td style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                                                        <span style={{ fontSize: '12px', background: '#f0f0f0', padding: '2px 7px', borderRadius: '10px', color: '#595959' }}>
                                                            {log.module || '—'}
                                                        </span>
                                                    </td>

                                                    <td style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                                                        <div style={{ fontWeight: '500', color: '#262626' }}>
                                                            {log.action || log.eventType}
                                                        </div>
                                                        {log.url && (
                                                            <div style={{ fontSize: '11px', color: '#bfbfbf', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '300px' }}>
                                                                {log.url}
                                                            </div>
                                                        )}
                                                    </td>

                                                    <td style={{ padding: '10px 8px', verticalAlign: 'middle', fontSize: '12px', color: '#8c8c8c' }}>
                                                        <i className={`fa ${log.deviceType === 'Mobile' ? 'fa-mobile' : log.deviceType === 'Tablet' ? 'fa-tablet' : 'fa-desktop'}`} style={{ marginRight: '4px' }}></i>
                                                        {log.deviceType || '—'}
                                                    </td>

                                                    <td style={{ padding: '10px 8px', verticalAlign: 'middle', fontSize: '12px', color: '#8c8c8c', fontFamily: 'monospace' }}>
                                                        {log.ipAddress || '—'}
                                                    </td>

                                                    <td style={{ padding: '10px 14px', verticalAlign: 'middle' }}>
                                                        {hasDetails && (
                                                            <button className="btn btn-default btn-xs"
                                                                onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                                                                title="View details">
                                                                <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                                                            </button>
                                                        )}
                                                    </td>

                                                </tr>

                                                {/* Expanded details row */}
                                                {isExpanded && details && (
                                                    <tr style={{ background: '#f8faff' }}>
                                                        <td colSpan={8} style={{ padding: '10px 24px 14px' }}>
                                                            <div style={{ fontSize: '12px', color: '#595959', fontWeight: '600', marginBottom: '6px' }}>
                                                                <i className="fa fa-info-circle" style={{ marginRight: '4px', color: '#1890ff' }}></i> Event Details
                                                            </div>
                                                            <div style={{
                                                                background: '#fff', border: '1px solid #e8e8e8', borderRadius: '6px',
                                                                padding: '10px 14px', display: 'grid',
                                                                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px',
                                                            }}>
                                                                {Object.entries(details).map(([k, v]) => (
                                                                    <div key={k}>
                                                                        <div style={{ fontSize: '11px', color: '#8c8c8c', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                            {k.replace(/_/g, ' ')}
                                                                        </div>
                                                                        <div style={{ fontSize: '13px', color: '#262626', wordBreak: 'break-all' }}>
                                                                            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}

                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px', color: '#8c8c8c' }}>
                                    {((pagination.current_page - 1) * pagination.per_page) + 1}–{Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total.toLocaleString()}
                                </span>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                    <button className="btn btn-default btn-sm"
                                        disabled={filters.p === 1}
                                        onClick={() => setFilters(p => ({ ...p, p: p.p - 1 }))}>
                                        <i className="fa fa-chevron-left"></i>
                                    </button>
                                    <span style={{ padding: '5px 12px', fontSize: '13px' }}>
                                        Page {filters.p} of {totalPages}
                                    </span>
                                    <button className="btn btn-default btn-sm"
                                        disabled={filters.p === totalPages}
                                        onClick={() => setFilters(p => ({ ...p, p: p.p + 1 }))}>
                                        <i className="fa fa-chevron-right"></i>
                                    </button>
                                </div>
                            </div>
                        )}

                    </div>
                </div>

            </div>
        </div>
    );
};

export default UserActivityReport;